<script setup>
/**
 * People, in groups that follow the session's format (People tab → "Different
 * groups for a webinar and a video conference"; prototype People, 27 Oct).
 *
 *   Webinar
 *     ON STAGE 3          You · Admin                  (You)
 *                         Maya Lewis · Admin           (Admin)
 *                         Dan Ruiz                     [Off stage]
 *     AUDIENCE 412        Ivy Chen  [hand 1]           [Bring on]
 *     The audience can't see each other, and can't speak until you bring
 *     somebody on stage.
 *
 *   Video conference (and Office Hours, and a breakout)
 *     IN THIS SESSION 4   You                          (You)
 *                         Maya Lewis   (Admin)  mic cam
 *                         Dan Ruiz   (Speaker)  mic cam  [✕]
 *
 * A raised hand is marked beside the name, wherever the person is listed,
 * with its place in the queue (HandMark; the order hands went up). It is a
 * signal to the host and nothing more: no duration, and in a webinar no route
 * to the stage of its own.
 *
 * Controls are for whoever runs the room, never on their own row, and nobody
 * can take another admin out. Everybody else sees the same groups without
 * them. Waiting to come in is drawn above this list (PeopleWaiting).
 *
 * On a phone (prototype Mobile Room, sheetPeople and dlg pAct) the host sees
 * Hands raised first — Lower, and in a webinar Bring on — then everybody
 * else; a row keeps its one stage move (Off stage, Bring on) and a ⋯ that
 * opens what else can be done with that person — Mute, the stage move, Lower
 * hand, Remove from session — with Mute everyone at the foot. Removing is
 * always asked, in a centred dialog.
 *
 * A webinar's audience never sees itself: an attendee off the stage sees who
 * is on it and "N others are watching", on any screen (prototype selfAud).
 */
import { computed, ref } from 'vue';
import XlAvatar from '../xl/XlAvatar.vue';
import XlButton from '../xl/XlButton.vue';
import SessionDialog from '../../livekit-client/components/SessionDialog.vue';
import MobileActionSheet from '../../livekit-client/components/MobileActionSheet.vue';
import HandMark from '../../livekit-client/components/HandMark.vue';
import { handPosition } from '../../helpers/handQueue';
import { peoplePanelGroups } from '../../helpers/sessionPanelScope';

const props = defineProps({
  /** The session's participants, you included (sessionStore.sessionParticipants). */
  people: { type: Array, default: () => [] },
  webinar: { type: Boolean, default: false },
  localIdentity: { type: String, default: null },
  onStageIdentities: { type: Array, default: () => [] },
  // Whoever runs the room: the only one with controls.
  canAct: { type: Boolean, default: false },
  /** Who has a hand up, first up first (sessionStore.handQueue). */
  handQueue: { type: Array, default: () => [] },
  // The room is on a phone: controls in a sheet per person.
  phone: { type: Boolean, default: false },
});

const emit = defineEmits(['bring-on', 'off-stage', 'remove', 'lower-hand', 'mute', 'mute-all']);

const handOf = (p) => (p.isHandRaised ? handPosition(props.handQueue, p.participantIdentity) : 0);

const panelGroups = computed(() => peoplePanelGroups(props.people, {
  webinar: props.webinar,
  localIdentity: props.localIdentity,
  onStageIdentities: props.onStageIdentities,
}));

// You, as the room has you: an attendee off a webinar's stage is in its audience.
const me = computed(() => props.people.find((p) => p.participantIdentity === props.localIdentity));
const inAudience = computed(() => props.webinar && me.value?.role === 'attendee'
  && !(props.onStageIdentities ?? []).includes(props.localIdentity));
// How many others watch, for that attendee: never who.
const othersWatching = computed(() => panelGroups.value.find((g) => g.key === 'audience')?.people.length ?? 0);

// A phone's host: the hands first, and nowhere else.
const handsUp = computed(() => (props.phone && props.canAct
  ? props.people.filter((p) => p.isHandRaised && !p.isScreenShareTile && !p.invisible && p.participantIdentity !== props.localIdentity)
    .sort((a, b) => handOf(a) - handOf(b))
  : []));

const allGroups = computed(() => {
  if (inAudience.value) {
    const stage = panelGroups.value.find((g) => g.key === 'stage');
    return [{ ...stage, people: stage.people.filter((p) => !isYou(p)) }];
  }
  if (!handsUp.value.length) return panelGroups.value;
  const up = new Set(handsUp.value.map((p) => p.participantIdentity));
  return [
    { key: 'hands', label: 'Hands raised', people: handsUp.value },
    ...panelGroups.value.map((g) => (g.key === 'stage' ? g : { ...g, people: g.people.filter((p) => !up.has(p.participantIdentity)) })),
  ];
});

/**
 * Finding somebody in a long audience (Webinar → let the host remove somebody
 * from the audience): six rows fit, two hundred do not, and the person who
 * needs removing is the one nobody can scroll to. The box appears once the
 * audience is longer than that; it narrows the audience, never the stage. The count stays the audience's.
 */
const AUDIENCE_FITS = 6;
const audienceQuery = ref('');
const audienceSize = computed(() => allGroups.value.find((g) => g.key === 'audience')?.people.length ?? 0);
const searchable = computed(() => props.webinar && (audienceSize.value > AUDIENCE_FITS || audienceQuery.value !== ''));
const matches = (p) => String(p.participantName ?? '').toLowerCase().includes(audienceQuery.value.trim().toLowerCase());
const groups = computed(() => allGroups.value.map((group) => ({
  ...group,
  count: group.people.length,
  people: searchable.value && audienceQuery.value.trim() && group.key === 'audience'
    ? group.people.filter(matches)
    : group.people,
})));

/**
 * Removing somebody. In a webinar it is asked first: the ✕ sits beside Bring
 * on, which does the opposite, and taking somebody out in front of a room is
 * not undone. A video conference keeps its ✕ as it always was.
 */
const removing = ref(null);
const askRemove = (p) => {
  acting.value = null;
  if (!props.webinar && !props.phone) {
    emit('remove', p.participantIdentity, p.participantName);
    return;
  }
  removing.value = { identity: p.participantIdentity, name: p.participantName };
};
const confirmRemove = () => {
  const who = removing.value;
  removing.value = null;
  if (who) emit('remove', who.identity, who.name);
};
const removable = (p) => controls(p) && p.role !== 'admin';

// A phone: what can be done with one person, in a sheet.
const acting = ref(null); // { p, group }
const onStage = (p) => p.role === 'admin' || p.role === 'speaker' || (props.onStageIdentities ?? []).includes(p.participantIdentity);
const stageAction = (p, group) => {
  if (!props.webinar) return null;
  if (group === 'stage' && p.role !== 'admin') return 'off-stage';
  if (group === 'audience' || (group === 'hands' && !onStage(p))) return 'bring-on';
  return null;
};
const hasActions = (p, group) => controls(p) && Boolean(stageAction(p, group) || removable(p) || !p.isMuted || p.isHandRaised);
const openActions = (p, group) => { if (props.phone && hasActions(p, group)) acting.value = { p, group }; };
const act = (what) => {
  const { p } = acting.value;
  acting.value = null;
  emit(what, p.participantIdentity);
};
const roleLine = (p, group) => {
  if (p.role === 'admin') return 'Host';
  if (onStage(p) && props.webinar) return group === 'stage' || p.role !== 'speaker' ? 'On stage' : 'Speaker';
  if (p.role === 'speaker') return 'Speaker';
  return props.webinar ? 'Attendee' : 'Participant';
};

const isYou = (p) => p.participantIdentity === props.localIdentity;
const controls = (p) => props.canAct && !isYou(p);


const note = computed(() => {
  if (inAudience.value) return `In a webinar, attendees can't see each other. ${othersWatching.value} ${othersWatching.value === 1 ? 'other is' : 'others are'} watching.`;
  if (props.webinar && props.canAct) return "The audience can't see each other, and can't speak until you bring somebody on stage.";
  return props.canAct
    ? 'Only the people in this session. You can remove anybody except another admin.'
    : "Only the people in this session. Who else is in the hub isn't shown here.";
});
</script>

<template>
  <div class="ppl-list">
    <template v-for="group in groups" :key="group.key">
      <div class="ppl-list__grp" :data-testid="`people-${group.key}-heading`">
        {{ group.label }} <span>{{ group.count }}</span>
      </div>
      <!-- A long audience: find somebody by name. -->
      <div v-if="group.key === 'audience' && searchable" class="ppl-list__find">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4 4" /></svg>
        <input
            v-model="audienceQuery"
            type="search"
            placeholder="Find somebody in the audience"
            aria-label="Find somebody in the audience"
            data-testid="people-audience-find"
        />
      </div>
      <p v-if="group.key === 'audience' && searchable && audienceQuery.trim() && !group.people.length" class="ppl-list__none" data-testid="people-audience-none">
        Nobody in the audience is called “{{ audienceQuery.trim() }}”.
      </p>
      <div class="ppl-list__rows" :data-testid="`people-${group.key}`">
        <div
            v-for="p in group.people"
            :key="`${group.key}-${p.participantIdentity}`"
            class="ppl-list__row"
            :class="{ 'ppl-list__row--tap': phone && hasActions(p, group.key) }"
            :role="phone && group.key === 'hands' && hasActions(p, group.key) ? 'button' : undefined"
            :tabindex="phone && group.key === 'hands' && hasActions(p, group.key) ? 0 : undefined"
            :aria-label="phone && group.key === 'hands' && hasActions(p, group.key) ? `What to do with ${p.participantName}` : undefined"
            @keydown.enter.self="openActions(p, group.key)"
            :data-testid="`people-row-${p.participantIdentity}`"
            @click="openActions(p, group.key)"
        >
          <XlAvatar :name="p.participantName" :src="p.avatar || ''" :size="32" />
          <div class="ppl-list__t">
            <!-- The hand inside the name, not after it: the column is a flex
                 column, so a mark of its own would stretch to its width. -->
            <b><span class="ppl-list__nm">{{ isYou(p) ? 'You' : p.participantName }}</span><HandMark
                v-if="p.isHandRaised"
                :position="handOf(p)"
                :name="isYou(p) ? '' : p.participantName"
            /></b>
            <span v-if="group.key === 'stage' && p.role === 'admin'">{{ phone ? 'Host' : 'Admin' }}</span>
          </div>

          <!-- Tags: who you are, and who runs or speaks. -->
          <span v-if="isYou(p)" class="ppl-list__tag ppl-list__tag--b">You</span>
          <span v-else-if="p.role === 'admin'" class="ppl-list__tag ppl-list__tag--b">{{ phone ? 'Host' : 'Admin' }}</span>
          <span v-else-if="p.role === 'speaker' && group.key === 'room'" class="ppl-list__tag">Speaker</span>

          <!-- A call: everybody's microphone and camera, as the room has them. -->
          <template v-if="group.key === 'room' && !isYou(p)">
            <span class="ppl-list__st" :title="p.isMuted ? 'Microphone off' : 'Microphone on'" :data-testid="`people-mic-${p.participantIdentity}`">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9.2" y="4.2" width="5.6" height="9.8" rx="2.8" /><path d="M6 11.4a6 6 0 0 0 12 0" /><path d="M12 17.4V19.8" /></svg>
              <i v-if="p.isMuted" />
            </span>
            <span class="ppl-list__st" :title="p.isCameraOff ? 'Camera off' : 'Camera on'" :data-testid="`people-cam-${p.participantIdentity}`">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3.5" y="6.5" width="12" height="11" rx="2.5" /><path d="M15.5 11.2 20.5 8.6v6.8l-5-2.6z" /></svg>
              <i v-if="p.isCameraOff" />
            </span>
          </template>

          <!-- A phone: the row's one move, then ⋯ for the rest. -->
          <template v-if="phone && controls(p)">
            <button v-if="group.key === 'hands'" type="button" class="ppl-list__btn ppl-list__btn--g" :title="`Lower ${p.participantName}'s hand`" data-testid="people-lower" @click.stop="emit('lower-hand', p.participantIdentity)">Lower</button>
            <button v-if="stageAction(p, group.key) === 'off-stage'" type="button" class="ppl-list__btn ppl-list__btn--g" :title="`Take ${p.participantName} off stage`" data-testid="people-off-stage" @click.stop="emit('off-stage', p.participantIdentity)">Off stage</button>
            <button v-else-if="stageAction(p, group.key) === 'bring-on'" type="button" class="ppl-list__btn" :title="`Bring ${p.participantName} on stage`" data-testid="people-bring-on" @click.stop="emit('bring-on', p.participantIdentity)">Bring on</button>
          </template>
          <!-- A hand's row shows only its two answers (prototype); the row itself opens the rest. -->
          <button
              v-if="phone && group.key !== 'hands' && hasActions(p, group.key)"
              type="button"
              class="ppl-list__more"
              :aria-label="`What to do with ${p.participantName}`"
              data-testid="people-actions"
              @click.stop="openActions(p, group.key)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5.5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="18.5" cy="12" r="1.7" /></svg>
          </button>
          <template v-else-if="controls(p) && !phone">
            <button v-if="group.key === 'stage' && p.role !== 'admin'" type="button" class="ppl-list__btn ppl-list__btn--g" :title="`Take ${p.participantName} off stage`" data-testid="people-off-stage" @click="emit('off-stage', p.participantIdentity)">Off stage</button>
            <button v-else-if="group.key === 'audience'" type="button" class="ppl-list__btn" :title="`Bring ${p.participantName} on stage`" data-testid="people-bring-on" @click="emit('bring-on', p.participantIdentity)">Bring on</button>
            <!-- A webinar row's way out, after the action it already has: the
                 same ✕ a call's rows carry. Never on you or another admin. -->
            <button
                v-if="group.key !== 'room' && removable(p)"
                type="button"
                class="ppl-list__x"
                :title="`Remove ${p.participantName} from the session`"
                :aria-label="`Remove ${p.participantName} from the session`"
                data-testid="people-remove"
                @click="askRemove(p)"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" aria-hidden="true"><path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8" /></svg>
            </button>
            <button
                v-else-if="group.key === 'room' && p.role !== 'admin'"
                type="button"
                class="ppl-list__x"
                title="Remove from the session"
                :aria-label="`Remove ${p.participantName} from the session`"
                data-testid="people-remove"
                @click="askRemove(p)"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" aria-hidden="true"><path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8" /></svg>
            </button>
          </template>
        </div>
      </div>
    </template>
    <p class="ppl-list__note" data-testid="people-note">{{ note }}</p>
    <!-- A phone's host: everybody's microphone, at the foot (prototype muteAll). -->
    <div v-if="phone && canAct" class="ppl-list__foot">
      <button type="button" class="ppl-list__mute-all" data-testid="people-mute-all" @click="emit('mute-all')">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9.2 7a2.8 2.8 0 0 1 5.6 0v4.2a2.8 2.8 0 0 1-5.6 0zM6 11.4a6 6 0 0 0 12 0M12 17.4v2.4M4 4l16 16" /></svg>
        Mute everyone
      </button>
    </div>

    <!-- prototype dropModal: asked, not done on the click -->
    <MobileActionSheet
        v-if="acting"
        :title="acting.p.participantName"
        :text="roleLine(acting.p, acting.group)"
        :person="acting.p.participantName"
        :person-src="acting.p.avatar || ''"
        data-testid="people-actions-sheet"
        @close="acting = null"
    >
      <div class="msa-list">
        <button v-if="!acting.p.isMuted" type="button" data-testid="people-sheet-mute" @click="act('mute')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9.2 7a2.8 2.8 0 0 1 5.6 0v4.2a2.8 2.8 0 0 1-5.6 0zM6 11.4a6 6 0 0 0 12 0M12 17.4v2.4M4 4l16 16" /></svg>
          <div>Mute</div>
        </button>
        <button v-if="stageAction(acting.p, acting.group) === 'bring-on'" type="button" data-testid="people-sheet-bring-on" @click="act('bring-on')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="10" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0M3 4l3 2M21 4l-3 2" /></svg>
          <div>Bring on stage<span>They can speak and turn on video</span></div>
        </button>
        <button v-else-if="stageAction(acting.p, acting.group) === 'off-stage'" type="button" data-testid="people-sheet-off-stage" @click="act('off-stage')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="8" r="3.4" /><path d="M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1M16 4.6a3.4 3.4 0 0 1 0 6.6M21 20v-1a5 5 0 0 0-3.5-4.8" /></svg>
          <div>Move to the audience</div>
        </button>
        <button v-if="acting.p.isHandRaised" type="button" data-testid="people-sheet-lower" @click="act('lower-hand')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 13V6.5a1.5 1.5 0 0 1 3 0V12M11 11.5V5a1.5 1.5 0 0 1 3 0v6.5M14 11.5V6.5a1.5 1.5 0 0 1 3 0V14a6 6 0 0 1-6 6h-.6a5 5 0 0 1-3.9-1.9L4 14.2a1.5 1.5 0 0 1 2.3-1.9L8 14" /></svg>
          <div>Lower hand</div>
        </button>
        <template v-if="removable(acting.p)">
          <hr>
          <button type="button" class="msa-bad" data-testid="people-sheet-remove" @click="askRemove(acting.p)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.8 7h14.4M9.6 7V5.2a1 1 0 0 1 1-1h2.8a1 1 0 0 1 1 1V7M6.6 7l.9 11.4a1.6 1.6 0 0 0 1.6 1.4h5.8a1.6 1.6 0 0 0 1.6-1.4L17.4 7" /></svg>
            <div>Remove from session</div>
          </button>
        </template>
      </div>
    </MobileActionSheet>

    <!-- On a phone: the mobile room's words, and Cancel, outside or Escape say no. -->
    <SessionDialog
        v-if="removing && phone"
        :title="`Remove ${removing.name}?`"
        tone="cr"
        icon="trash"
        backdrop-closes
        escape-closes
        data-testid="people-remove-confirm"
        @close="removing = null"
    >
      <p>They're told kindly and can come back in through the waiting room.</p>
      <template #actions>
        <XlButton variant="ghost" data-testid="people-remove-cancel" @click="removing = null">Cancel</XlButton>
        <XlButton variant="danger" data-testid="people-remove-yes" @click="confirmRemove">Remove</XlButton>
      </template>
    </SessionDialog>
    <SessionDialog v-else-if="removing" :title="`Remove ${removing.name}?`" tone="cr" icon="info" data-testid="people-remove-confirm">
      <p>They're taken out of the session straight away. They can't come back unless you let them in again.</p>
      <template #actions>
        <XlButton variant="ghost" data-testid="people-remove-cancel" @click="removing = null">Cancel</XlButton>
        <XlButton variant="danger" data-testid="people-remove-yes" @click="confirmRemove">Remove</XlButton>
      </template>
    </SessionDialog>
  </div>
</template>

<style scoped>
/* The prototype's .rmgrp, .rmppl, .rmrow, .tag, .rmst and .rmnote. Doubled
   class: the panel's older chat.scss styles buttons and divs deep inside the
   people list. */
.ppl-list.ppl-list { font-family: var(--xl-font-family); }

.ppl-list .ppl-list__grp {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--xl-color-outline-variant, #B6AEBC);
}

.ppl-list .ppl-list__grp span { font-size: 11px; font-weight: 700; color: var(--xl-color-outline, #867D8D); }


.ppl-list .ppl-list__rows { padding: 4px 14px; }

.ppl-list .ppl-list__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid var(--xl-color-border-subtle, #F6F2F8);
}

.ppl-list .ppl-list__row:last-child { border-bottom: none; }
.ppl-list .ppl-list__row--tap { cursor: pointer; }

/* A phone's ⋯: the way to a person's actions. */
.ppl-list .ppl-list__more {
  display: grid;
  flex: 0 0 36px;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #F4F1F6;
  color: #514A57;
  cursor: pointer;
}
.ppl-list .ppl-list__foot { padding: 4px 14px 14px; }
.ppl-list .ppl-list__mute-all {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  height: 44px;
  border: 1px solid #E2DBE6;
  border-radius: 99px;
  background: #fff;
  color: var(--xl-color-on-surface, #1C1720);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.ppl-list .ppl-list__mute-all:focus-visible { outline: 2px solid var(--xl-color-primary, #5E2F78); outline-offset: 2px; }
.ppl-list .ppl-list__more:focus-visible { outline: 2px solid var(--xl-color-primary, #5E2F78); outline-offset: 2px; }

.ppl-list .ppl-list__t { flex: 1; min-width: 0; }
/* A row: the name gives way to an ellipsis, the hand mark beside it never does. */
.ppl-list .ppl-list__t b {
  display: flex;
  align-items: center;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--xl-color-on-surface, #1C1720);
}
.ppl-list .ppl-list__nm { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ppl-list .ppl-list__t > span { display: block; font-size: 11.5px; color: var(--xl-color-outline, #867D8D); }

/* prototype .tag / .tag.b */
.ppl-list .ppl-list__tag {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  height: 23px;
  padding: 0 9px;
  border-radius: 7px;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  background: var(--xl-color-border-subtle, #F6F2F8);
  color: var(--xl-color-on-surface-variant, #514A57);
}

.ppl-list .ppl-list__tag--b { background: var(--xl-color-primary-container, #F5EFF9); color: var(--xl-color-primary, #5E2F78); }

/* prototype .rmst: the state, struck through in red when it is off */
.ppl-list .ppl-list__st {
  position: relative;
  flex: 0 0 20px;
  width: 20px;
  display: grid;
  place-items: center;
  color: var(--xl-color-outline, #867D8D);
}

.ppl-list .ppl-list__st i {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 17px;
  height: 1.5px;
  background: var(--xl-color-error, #A32020);
  transform: rotate(-45deg);
}

/* prototype .rmppl .rmrow .btn.sm: the primary at row size; .btn.g the quiet one */
.ppl-list .ppl-list__btn {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 13px;
  border: 0;
  border-radius: 99px;
  background: linear-gradient(155deg, var(--xl-color-surface-tint, #8154A6), var(--xl-color-primary, #5E2F78));
  color: #fff;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 5px 16px rgba(94, 47, 120, 0.26);
  cursor: pointer;
}

.ppl-list .ppl-list__btn--g {
  border: 1px solid var(--xl-color-border, #EDE7F0);
  background: #fff;
  color: var(--xl-color-on-surface, #1C1720);
  box-shadow: 0 1px 2px rgba(28, 23, 32, 0.04);
}

.ppl-list .ppl-list__btn--g:hover { border-color: var(--xl-color-surface-tint, #8154A6); color: var(--xl-color-primary, #5E2F78); }

/* Removing somebody: the outlined red ✕ (.ib, color --cr), as Not letting them in. */
.ppl-list .ppl-list__x {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--xl-color-border, #EDE7F0);
  border-radius: 11px;
  background: #fff;
  color: var(--xl-color-error, #A32020);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.ppl-list .ppl-list__x:hover { background: var(--xl-color-error-container, #FCEBEB); }

.ppl-list .ppl-list__btn:focus-visible,
.ppl-list .ppl-list__x:focus-visible { outline: 2px solid var(--xl-color-primary, #5E2F78); outline-offset: 2px; }

/* prototype .rmfind: the audience's search, under its heading */
.ppl-list .ppl-list__find {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 14px 6px;
  padding: 0 11px;
  height: 34px;
  border: 1px solid var(--xl-color-border, #EDE7F0);
  border-radius: 10px;
  background: #fff;
  color: var(--xl-color-outline, #867D8D);
}

.ppl-list .ppl-list__find input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  color: var(--xl-color-on-surface, #1C1720);
}

.ppl-list .ppl-list__find:focus-within { border-color: var(--xl-color-primary, #5E2F78); }

.ppl-list .ppl-list__none { margin: 4px 16px 8px; font-size: 12.5px; color: var(--xl-color-outline, #867D8D); }

/* prototype .rmnote */
.ppl-list .ppl-list__note {
  margin: 0;
  padding: 10px 16px 14px;
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--xl-color-outline-variant, #B6AEBC);
}
</style>
